import * as _ from 'underscore';

import { incrementRouterSpaceReactKey } from '../../app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import { appStore } from '../../app/AppStore';
import { FeatureFlags } from '../FeatureFlags';
import { ILocLinkifierModel } from '../filters/LocLinkifierFilter';
import { ResultHelper } from '../helpers/ResultHelper';
import { L10n } from '../L10n/L10n';
import { IDataSourceDetailInfo } from '../models/IDataSourceInfo';
import { Module } from '../Module';
import { RemoteRefreshAgents } from '../resources/RemoteRefreshAgents';
import { IGetRemoteRefreshAgentsResult, IRemoteRefreshAgent } from '../ServerApi/requests/GetRemoteRefreshAgentsRequest';
import { RefreshModeTypes } from '../ServerApi/types/IRemoteRefreshProperties';
import { IResult } from '../ServerApi/types/IResponse';
import { ChangeDatasourceRefreshModeAction } from './ChangeDatasourceRefreshModeAction';
import { ChangeDatasourceRemoteAgentAction } from './ChangeDatasourceRemoteAgentAction';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { HelpService } from '../../commonInfra/services/HelpService';

export interface IRemoteRefreshPropertiesEditModel {
  refreshMode: string;
  remoteAgentName: string;
}

export interface IRemoteRefreshAgentOption {
  display: string;
  name: string;
  schedulesSyncedAt?: string;   // ISO 8601 formatted string, or null
  launchesSyncClient?: boolean;
  remoteAgentTimeZone?: string; // The time zone of the Tableau Bridge client
}

export interface IChangeDatasourceRemoteRefreshPropertiesScope extends ng.IScope {
  prompt: string;
  remoteRefreshModeOptions: string[];
  remoteAgentOptions: IRemoteRefreshAgentOption[];
  remoteAgentOptionsEnabled: () => boolean;
  remoteRefreshEnabled: () => boolean;
  remoteRefreshDescription: () => ILocLinkifierModel;
  existingRefreshSchedules: () => number;
  warnRefreshesToBeDeleted: () => boolean;
  warnEmbeddedPassword: () => boolean;
  remoteAgentSelectClicked: () => void;
  bridgeServerSideRefreshEnabled: () => boolean;
  liveQueryEnabledForSite: () => boolean;
  hasBridgeClientToServeLiveQuery: () => boolean;

  editModel: IRemoteRefreshPropertiesEditModel;
}

export class ChangeDatasourceRemoteRefreshPropertiesAction {

  public static $inject = [
    '$rootScope',
    '$q',
    '$translate',
    '$window',
    'ConfirmActionDialog',
    'RemoteRefreshAgents',
    'ChangeDatasourceRefreshModeAction',
    'ChangeDatasourceRemoteAgentAction'
  ];

  constructor(
    private $rootScope: ng.IRootScopeService,
    private $q: ng.IQService,
    private $translate: ng.translate.ITranslateService,
    private $window: ng.IWindowService,
    private ConfirmActionDialog: ConfirmActionDialog,
    private RemoteRefreshAgents: RemoteRefreshAgents,
    private ChangeDatasourceRefreshModeAction: ChangeDatasourceRefreshModeAction,
    private ChangeDatasourceRemoteAgentAction: ChangeDatasourceRemoteAgentAction) { }

  public execute(datasource: IDataSourceDetailInfo, existingRefreshSchedulesCount: number): ng.IPromise<any> {
    const dialogScope = <IChangeDatasourceRemoteRefreshPropertiesScope> this.$rootScope.$new();

    dialogScope.prompt = this.$translate.instant('editRemoteRefreshPropertiesDialogPrompt', {DATASOURCE_NAME: L10n.elideIfLong(datasource.name)});

    // careful, the UI depends on Server being the first item in the array, remote the second.
    dialogScope.remoteRefreshModeOptions = [ RefreshModeTypes.Server, RefreshModeTypes.Remote ];
    dialogScope.remoteAgentOptions = [];
    dialogScope.remoteAgentOptionsEnabled = () => dialogScope.editModel.refreshMode === RefreshModeTypes.Remote;
    dialogScope.remoteRefreshEnabled = () => dialogScope.remoteAgentOptions && dialogScope.remoteAgentOptions.length > 0;

    dialogScope.remoteRefreshDescription = () => {
      return {
        localizedStatementKey: 'remoteRefreshModeEditRemoteDescription',
        links: [{
          key: '{LEARN_HOW_TO_ADD_A_COMPUTER_TO_THE_LIST}',
          localizedValueKey: 'remoteRefreshModeEditRemoteDescriptionSetupLink',
          url: HelpService.getServerHelpUrlByCategory('bridge-add-client')
        }]
      };
    };

    dialogScope.editModel = { refreshMode: datasource.refreshMode, remoteAgentName: datasource.remoteAgentName };

    dialogScope.existingRefreshSchedules = () => existingRefreshSchedulesCount;
    dialogScope.warnRefreshesToBeDeleted = () => {
      return datasource.refreshMode === RefreshModeTypes.Server &&
        dialogScope.editModel.refreshMode === RefreshModeTypes.Remote &&
        existingRefreshSchedulesCount > 0;
    };
    dialogScope.warnEmbeddedPassword = () => {
      return datasource.refreshMode === RefreshModeTypes.Server &&
        dialogScope.editModel.refreshMode === RefreshModeTypes.Remote &&
        datasource.connectionDetails &&
        datasource.connectionDetails.hasEmbeddedPassword;
    };

    dialogScope.remoteAgentSelectClicked = () => this.updateAvailableRemoteAgentList(datasource, dialogScope);

    dialogScope.bridgeServerSideRefreshEnabled = () => {
      return FeatureFlags.bridgeServerSideRefresh && datasource.usingRemoteQueryAgent;
    };

    // TODO fuliu:
    // If desired by design, need to read the site setting to determine. Assume it's true for now
    dialogScope.liveQueryEnabledForSite = () => {
      return true;
    };

    // TODO fuliu:
    // If desired by design, need to check if there is any agent configured to serve live queries.
    // Assume it's true for now.
    dialogScope.hasBridgeClientToServeLiveQuery = () => {
      return true;
    };

    dialogScope.$watch('editModel.remoteAgentName', (newRemoteAgent: string, oldRemoteAgent: string) => {
      if (newRemoteAgent === oldRemoteAgent) {
        return;
      }
    });

    this.updateAvailableRemoteAgentList(datasource, dialogScope);

    return this.ConfirmActionDialog.open({
      scope: dialogScope,
      title: this.$translate.instant('editRemoteRefreshPropertiesDialogTitle'),
      bodyTemplateUrl: 'confirmActionDialogRemoteRefreshProperties.html',
      size: '400px',
      actionName: this.$translate.instant('save'),
      action: angular.noop,
      confirmDisabled: () => {
        return dialogScope.editModel.refreshMode === RefreshModeTypes.Remote && !dialogScope.editModel.remoteAgentName;
      }
    }).result
      .then((result: any) => {
        // We should show success toasts from the change mode action if the requested refresh mode is Server,
        // because the change agent action does nothing, including showing a toast, if so.
        const showSuccessToast = dialogScope.editModel.refreshMode === RefreshModeTypes.Server;
        // There's a race condition in the back-end, send the two requests sequentially.
        return this.ChangeDatasourceRefreshModeAction.execute(datasource, dialogScope.editModel.refreshMode, dialogScope.editModel.remoteAgentName, !showSuccessToast, false);
      })
      .then(ResultHelper.rejectResultOnError(this.$q))
      .then((result: IResult) => {
        return this.ChangeDatasourceRemoteAgentAction.execute(datasource, dialogScope.editModel.remoteAgentName, dialogScope.editModel.refreshMode, false);
      })
      .then(ResultHelper.rejectResultOnError(this.$q))
      .finally(() => {
        dialogScope.$destroy();
        appStore.dispatch(incrementRouterSpaceReactKey());
      });
  }

  private updateAvailableRemoteAgentList(datasource: IDataSourceDetailInfo, scope: IChangeDatasourceRemoteRefreshPropertiesScope) {
    return this.populateAvailableRemoteAgentOptions(datasource, scope)
      .then(() => {

        if (!datasource.remoteAgentName && datasource.refreshMode === RefreshModeTypes.Server) {
          // automatically try to select the first available agent if the current refresh mode is server
          // if the current refresh mode is remote and there isn't a remote agent specified the previously specified agent was unlinked
          if (scope.remoteAgentOptions.length > 0) {
            scope.editModel.remoteAgentName = scope.remoteAgentOptions[0].name;
          }
        }

      });
  }

  // Add agent options to scope
  public populateAvailableRemoteAgentOptions(datasource: IDataSourceDetailInfo, scope: IChangeDatasourceRemoteRefreshPropertiesScope) {
    return this.RemoteRefreshAgents.get()
      .then((result: IGetRemoteRefreshAgentsResult) => {

        const remoteAgents = ChangeDatasourceRemoteRefreshPropertiesAction.buildRemoteAgentsList(result, datasource);

        // Add the options representing remote agents
        scope.remoteAgentOptions = remoteAgents;

        return scope;
      });
  }

  public static buildRemoteAgentsList(remoteAgentResult: IGetRemoteRefreshAgentsResult, datasource?: IDataSourceDetailInfo): IRemoteRefreshAgentOption[] {
    let listContainsExistingAgent = false;

    const agentList: IRemoteRefreshAgentOption[] = _.map(remoteAgentResult.agents, (agent: IRemoteRefreshAgent) => {
      if (datasource && datasource.remoteAgentName && datasource.remoteAgentName === agent.name){
        listContainsExistingAgent = true;
      }

      return {
        display: agent.name,
        name: agent.name,
        schedulesSyncedAt: agent.schedulesSyncedAt,
        remoteAgentTimeZone: agent.remoteAgentTimeZone
      };
    });

    if (!listContainsExistingAgent && datasource && datasource.remoteAgentName) {
      agentList.unshift({
        display: datasource.remoteAgentName,
        name: datasource.remoteAgentName
      });
    }

    return agentList;
  }
}

Module.instance().service('ChangeDatasourceRemoteRefreshPropertiesAction', ChangeDatasourceRemoteRefreshPropertiesAction);
